import { useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, Languages, LogOut, ChevronDown, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BrandLogo } from "@/components/common/BrandLogo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationsDialog from "@/components/layout/NotificationsDialog";
import NotificationInbox from "@/components/layout/NotificationInbox";
import { OPS } from "@/constants/testIds";

function useTitle() {
    const { pathname } = useLocation();
    const { t } = useI18n();
    if (pathname.startsWith("/sales")) return t("sales_title");
    if (pathname.startsWith("/admin/users")) return t("users_title");
    if (pathname.startsWith("/admin/branches")) return t("branches_title");
    return t("app_name");
}

function initials(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

export function Topbar({ onMenu }) {
    const { me, logout, isSuperAdmin } = useAuth();
    const { t, toggleLang } = useI18n();
    const title = useTitle();
    const user = me?.user;
    const [notifOpen, setNotifOpen] = useState(false);

    return (
        <header
            data-testid={OPS.shell.topbar}
            className="sticky top-0 z-20 h-16 shrink-0 bg-[color:var(--bg-surface)]/85 backdrop-blur-md border-b border-[color:var(--border-soft)] flex items-center gap-3 px-4 sm:px-6"
        >
            <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={onMenu}
                data-testid={OPS.shell.mobileMenu}
                aria-label="menu"
            >
                <Menu className="h-5 w-5" />
            </Button>

            <BrandLogo className="h-8 w-8 object-contain shrink-0 lg:hidden" />

            <h1 className="text-lg font-bold text-[color:var(--ink)] truncate">{title}</h1>

            <div className="ms-auto flex items-center gap-2">
                <NotificationInbox />
                <Button
                    variant="soft"
                    size="sm"
                    onClick={toggleLang}
                    data-testid={OPS.shell.langToggle}
                    className="gap-2"
                >
                    <Languages className="h-4 w-4" />
                    <span className="text-sm">{t("language")}</span>
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid={OPS.shell.userMenu}
                            className="flex items-center gap-2 rounded-full pe-3 ps-1 py-1 hover:bg-[color:var(--brand-orange-soft)] transition-colors duration-150"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--brand-brown)] text-white text-xs font-bold uppercase shadow-[var(--shadow-sm)]">
                                {initials(user?.name)}
                            </span>
                            <span className="hidden sm:flex flex-col items-start leading-tight">
                                <span className="text-sm font-semibold text-[color:var(--ink)] max-w-[160px] truncate">
                                    {user?.name}
                                </span>
                                <span className="text-[11px] text-[color:var(--ink-muted)]">
                                    {isSuperAdmin ? t("role_super_admin") : t("role_user")}
                                </span>
                            </span>
                            <ChevronDown className="h-4 w-4 text-[color:var(--ink-muted)] hidden sm:block" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="flex flex-col gap-1 pb-2">
                            <span className="font-semibold text-[color:var(--ink)]">{user?.name}</span>
                            <span className="text-xs font-normal text-[color:var(--ink-muted)] break-all">
                                <bdi dir="ltr">{user?.email || user?.national_id}</bdi>
                            </span>
                            <Badge variant="brown" className="w-fit mt-1.5">
                                {isSuperAdmin ? t("role_super_admin") : t("role_user")}
                            </Badge>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid={OPS.shell.notificationsMenuItem}
                            onClick={() => setNotifOpen(true)}
                            className="cursor-pointer gap-2"
                        >
                            <Bell className="h-4 w-4" />
                            {t("notif_settings")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            data-testid={OPS.shell.logout}
                            onClick={logout}
                            className="text-[color:var(--danger)] focus:text-[color:var(--danger)] cursor-pointer gap-2"
                        >
                            <LogOut className="h-4 w-4" />
                            {t("logout")}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <NotificationsDialog open={notifOpen} onOpenChange={setNotifOpen} />
        </header>
    );
}
